import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  Loader2,
  FileText,
  Mail,
  Clock,
  Sparkles,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

interface ExpertCompletionScreenProps {
  interview: {
    expertName: string;
    expertEmail: string;
    journalist: {
      name: string;
    };
    siteDomain: string;
    siteName?: string;
    language?: string;
  };
  token: string;
  status: 'processing' | 'complete';
}

// Translations for the completion screen
const translations: Record<string, Record<string, string>> = {
  en: {
    processingTitle: 'Processing Your Interview',
    processingSubtitle: "We're processing your recording and preparing your interview content",
    recordingUploaded: 'Recording Uploaded',
    recordingUploadedDesc: 'Your interview has been securely uploaded',
    transcribingAudio: 'Transcribing Audio',
    transcribingAudioDesc: 'Converting speech to text using AI',
    processingComplete: 'Processing Complete',
    transcriptionComplete: 'Transcription and analysis complete!',
    processingInterview: 'Processing your interview...',
    preparingProcess: 'Preparing to process',
    overallProgress: 'Overall Progress',
    processingTime: "This usually takes 5-10 minutes. Feel free to close this window - we'll email you when it's ready.",
    interviewComplete: 'Interview Complete!',
    thankYou: 'Thank you for your time',
    successMessage: 'Your interview has been processed successfully! The team will review and prepare the final article.',
    interviewContent: 'Interview Content',
    transcript: 'Transcript',
    generatedArticle: 'Generated Article',
    whatHappensNext: 'What Happens Next?',
    articleReview: 'Article Review',
    articleReviewDesc: 'will review and edit the interview into a polished article',
    expertApproval: 'Expert Approval',
    expertApprovalDesc: "You'll receive a draft for your review and approval before publication",
    publication: 'Publication',
    publicationDesc: 'Once approved, your interview will be published on',
    theSite: 'the site',
    checkEmail: 'Check Your Email',
    emailUpdates: "We'll send updates and the draft article to",
    yourEmail: 'your registered email',
    confirmCompletion: 'Confirm Completion',
    submitting: 'Submitting...',
    thankYouSubmitted: 'Thank you! Your interview has been submitted successfully.',
    allDone: 'All Done!',
    closeWindow: "You can now close this window. We'll be in touch soon!",
    // Redo interview
    redoInterview: 'Record Again',
    redoDescription: 'Not satisfied with your recording? You can redo the interview.',
    redoWarning: 'Your previous recording will be archived but kept for reference.',
    redoConfirmTitle: 'Redo Interview?',
    redoConfirmMessage: 'This will archive your current interview and start a fresh recording session. Your previous recording will be kept but not used.',
    redoCancel: 'Keep Current',
    redoConfirm: 'Yes, Start Over',
    redoing: 'Preparing new interview...',
    redoSuccess: 'New interview created! Redirecting...',
    redoError: 'Failed to create new interview. Please try again.',
  },
  es: {
    processingTitle: 'Procesando Tu Entrevista',
    processingSubtitle: 'Estamos procesando tu grabación y preparando el contenido de tu entrevista',
    recordingUploaded: 'Grabación Subida',
    recordingUploadedDesc: 'Tu entrevista ha sido subida de forma segura',
    transcribingAudio: 'Transcribiendo Audio',
    transcribingAudioDesc: 'Convirtiendo voz a texto usando IA',
    processingComplete: 'Procesamiento Completo',
    transcriptionComplete: '¡Transcripción y análisis completados!',
    processingInterview: 'Procesando tu entrevista...',
    preparingProcess: 'Preparando para procesar',
    overallProgress: 'Progreso General',
    processingTime: 'Esto suele tardar 5-10 minutos. Puedes cerrar esta ventana - te enviaremos un email cuando esté listo.',
    interviewComplete: '¡Entrevista Completada!',
    thankYou: 'Gracias por tu tiempo',
    successMessage: '¡Tu entrevista ha sido procesada con éxito! El equipo revisará y preparará el artículo final.',
    interviewContent: 'Contenido de la Entrevista',
    transcript: 'Transcripción',
    generatedArticle: 'Artículo Generado',
    whatHappensNext: '¿Qué Pasa Ahora?',
    articleReview: 'Revisión del Artículo',
    articleReviewDesc: 'revisará y editará la entrevista para crear un artículo pulido',
    expertApproval: 'Aprobación del Experto',
    expertApprovalDesc: 'Recibirás un borrador para tu revisión y aprobación antes de la publicación',
    publication: 'Publicación',
    publicationDesc: 'Una vez aprobado, tu entrevista será publicada en',
    theSite: 'el sitio',
    checkEmail: 'Revisa Tu Email',
    emailUpdates: 'Te enviaremos actualizaciones y el borrador del artículo a',
    yourEmail: 'tu email registrado',
    confirmCompletion: 'Confirmar Finalización',
    submitting: 'Enviando...',
    thankYouSubmitted: '¡Gracias! Tu entrevista ha sido enviada con éxito.',
    allDone: '¡Todo Listo!',
    closeWindow: 'Ya puedes cerrar esta ventana. ¡Estaremos en contacto pronto!',
    // Redo interview
    redoInterview: 'Grabar de Nuevo',
    redoDescription: '¿No estás satisfecho con tu grabación? Puedes rehacer la entrevista.',
    redoWarning: 'Tu grabación anterior será archivada pero se mantendrá como referencia.',
    redoConfirmTitle: '¿Rehacer Entrevista?',
    redoConfirmMessage: 'Esto archivará tu entrevista actual y comenzará una nueva sesión de grabación. Tu grabación anterior se conservará pero no se utilizará.',
    redoCancel: 'Mantener Actual',
    redoConfirm: 'Sí, Empezar de Nuevo',
    redoing: 'Preparando nueva entrevista...',
    redoSuccess: '¡Nueva entrevista creada! Redirigiendo...',
    redoError: 'Error al crear nueva entrevista. Por favor, inténtalo de nuevo.',
  },
  ca: {
    processingTitle: 'Processant la Teva Entrevista',
    processingSubtitle: 'Estem processant la teva gravació i preparant el contingut de la teva entrevista',
    recordingUploaded: 'Gravació Pujada',
    recordingUploadedDesc: 'La teva entrevista ha estat pujada de forma segura',
    transcribingAudio: 'Transcrivint Àudio',
    transcribingAudioDesc: 'Convertint veu a text utilitzant IA',
    processingComplete: 'Processament Complet',
    transcriptionComplete: 'Transcripció i anàlisi completats!',
    processingInterview: 'Processant la teva entrevista...',
    preparingProcess: 'Preparant per processar',
    overallProgress: 'Progrés General',
    processingTime: 'Això sol trigar 5-10 minuts. Pots tancar aquesta finestra - t\'enviarem un email quan estigui llest.',
    interviewComplete: 'Entrevista Completada!',
    thankYou: 'Gràcies pel teu temps',
    successMessage: 'La teva entrevista ha estat processada amb èxit! L\'equip revisarà i prepararà l\'article final.',
    interviewContent: 'Contingut de l\'Entrevista',
    transcript: 'Transcripció',
    generatedArticle: 'Article Generat',
    whatHappensNext: 'Què Passa Ara?',
    articleReview: 'Revisió de l\'Article',
    articleReviewDesc: 'revisarà i editarà l\'entrevista per crear un article polit',
    expertApproval: 'Aprovació de l\'Expert',
    expertApprovalDesc: 'Rebràs un esborrany per a la teva revisió i aprovació abans de la publicació',
    publication: 'Publicació',
    publicationDesc: 'Un cop aprovat, la teva entrevista serà publicada a',
    theSite: 'el lloc',
    checkEmail: 'Revisa el Teu Email',
    emailUpdates: 'T\'enviarem actualitzacions i l\'esborrany de l\'article a',
    yourEmail: 'el teu email registrat',
    confirmCompletion: 'Confirmar Finalització',
    submitting: 'Enviant...',
    thankYouSubmitted: 'Gràcies! La teva entrevista ha estat enviada amb èxit.',
    allDone: 'Tot Llest!',
    closeWindow: 'Ja pots tancar aquesta finestra. Estarem en contacte aviat!',
    // Redo interview
    redoInterview: 'Gravar de Nou',
    redoDescription: 'No estàs satisfet amb la teva gravació? Pots refer l\'entrevista.',
    redoWarning: 'La teva gravació anterior serà arxivada però es mantindrà com a referència.',
    redoConfirmTitle: 'Refer Entrevista?',
    redoConfirmMessage: 'Això arxivarà la teva entrevista actual i començarà una nova sessió de gravació. La teva gravació anterior es conservarà però no s\'utilitzarà.',
    redoCancel: 'Mantenir Actual',
    redoConfirm: 'Sí, Començar de Nou',
    redoing: 'Preparant nova entrevista...',
    redoSuccess: 'Nova entrevista creada! Redirigint...',
    redoError: 'Error en crear nova entrevista. Si us plau, torna-ho a provar.',
  },
  fr: {
    processingTitle: 'Traitement de Votre Interview',
    processingSubtitle: 'Nous traitons votre enregistrement et préparons le contenu de votre interview',
    recordingUploaded: 'Enregistrement Téléchargé',
    recordingUploadedDesc: 'Votre interview a été téléchargée en toute sécurité',
    transcribingAudio: 'Transcription Audio',
    transcribingAudioDesc: 'Conversion de la parole en texte avec l\'IA',
    processingComplete: 'Traitement Terminé',
    transcriptionComplete: 'Transcription et analyse terminées !',
    processingInterview: 'Traitement de votre interview...',
    preparingProcess: 'Préparation du traitement',
    overallProgress: 'Progression Globale',
    processingTime: 'Cela prend généralement 5-10 minutes. Vous pouvez fermer cette fenêtre - nous vous enverrons un email quand ce sera prêt.',
    interviewComplete: 'Interview Terminée !',
    thankYou: 'Merci pour votre temps',
    successMessage: 'Votre interview a été traitée avec succès ! L\'équipe va réviser et préparer l\'article final.',
    interviewContent: 'Contenu de l\'Interview',
    transcript: 'Transcription',
    generatedArticle: 'Article Généré',
    whatHappensNext: 'Et Maintenant ?',
    articleReview: 'Révision de l\'Article',
    articleReviewDesc: 'révisera et éditera l\'interview pour créer un article soigné',
    expertApproval: 'Approbation de l\'Expert',
    expertApprovalDesc: 'Vous recevrez un brouillon pour votre révision et approbation avant publication',
    publication: 'Publication',
    publicationDesc: 'Une fois approuvé, votre interview sera publiée sur',
    theSite: 'le site',
    checkEmail: 'Vérifiez Votre Email',
    emailUpdates: 'Nous vous enverrons les mises à jour et le brouillon de l\'article à',
    yourEmail: 'votre email enregistré',
    confirmCompletion: 'Confirmer la Fin',
    submitting: 'Envoi...',
    thankYouSubmitted: 'Merci ! Votre interview a été soumise avec succès.',
    allDone: 'Tout est Fait !',
    closeWindow: 'Vous pouvez maintenant fermer cette fenêtre. Nous vous contacterons bientôt !',
    // Redo interview
    redoInterview: 'Enregistrer à Nouveau',
    redoDescription: 'Pas satisfait de votre enregistrement ? Vous pouvez refaire l\'interview.',
    redoWarning: 'Votre enregistrement précédent sera archivé mais conservé comme référence.',
    redoConfirmTitle: 'Refaire l\'Interview ?',
    redoConfirmMessage: 'Cela archivera votre interview actuelle et démarrera une nouvelle session d\'enregistrement. Votre enregistrement précédent sera conservé mais non utilisé.',
    redoCancel: 'Garder l\'Actuel',
    redoConfirm: 'Oui, Recommencer',
    redoing: 'Préparation de la nouvelle interview...',
    redoSuccess: 'Nouvelle interview créée ! Redirection...',
    redoError: 'Échec de la création de la nouvelle interview. Veuillez réessayer.',
  },
  de: {
    processingTitle: 'Verarbeitung Ihres Interviews',
    processingSubtitle: 'Wir verarbeiten Ihre Aufnahme und bereiten den Interview-Inhalt vor',
    recordingUploaded: 'Aufnahme Hochgeladen',
    recordingUploadedDesc: 'Ihr Interview wurde sicher hochgeladen',
    transcribingAudio: 'Audio Transkribieren',
    transcribingAudioDesc: 'Sprache wird mit KI in Text umgewandelt',
    processingComplete: 'Verarbeitung Abgeschlossen',
    transcriptionComplete: 'Transkription und Analyse abgeschlossen!',
    processingInterview: 'Verarbeitung Ihres Interviews...',
    preparingProcess: 'Vorbereitung der Verarbeitung',
    overallProgress: 'Gesamtfortschritt',
    processingTime: 'Dies dauert normalerweise 5-10 Minuten. Sie können dieses Fenster schließen - wir senden Ihnen eine E-Mail, wenn es fertig ist.',
    interviewComplete: 'Interview Abgeschlossen!',
    thankYou: 'Vielen Dank für Ihre Zeit',
    successMessage: 'Ihr Interview wurde erfolgreich verarbeitet! Das Team wird den finalen Artikel überprüfen und vorbereiten.',
    interviewContent: 'Interview-Inhalt',
    transcript: 'Transkript',
    generatedArticle: 'Generierter Artikel',
    whatHappensNext: 'Was Passiert als Nächstes?',
    articleReview: 'Artikel-Überprüfung',
    articleReviewDesc: 'wird das Interview überprüfen und zu einem ausgefeilten Artikel bearbeiten',
    expertApproval: 'Experten-Genehmigung',
    expertApprovalDesc: 'Sie erhalten einen Entwurf zur Überprüfung und Genehmigung vor der Veröffentlichung',
    publication: 'Veröffentlichung',
    publicationDesc: 'Nach der Genehmigung wird Ihr Interview veröffentlicht auf',
    theSite: 'der Website',
    checkEmail: 'Prüfen Sie Ihre E-Mail',
    emailUpdates: 'Wir senden Updates und den Artikelentwurf an',
    yourEmail: 'Ihre registrierte E-Mail',
    confirmCompletion: 'Abschluss Bestätigen',
    submitting: 'Wird gesendet...',
    thankYouSubmitted: 'Danke! Ihr Interview wurde erfolgreich eingereicht.',
    allDone: 'Alles Erledigt!',
    closeWindow: 'Sie können dieses Fenster jetzt schließen. Wir melden uns bald!',
    // Redo interview
    redoInterview: 'Erneut Aufnehmen',
    redoDescription: 'Nicht zufrieden mit Ihrer Aufnahme? Sie können das Interview wiederholen.',
    redoWarning: 'Ihre vorherige Aufnahme wird archiviert, aber als Referenz aufbewahrt.',
    redoConfirmTitle: 'Interview Wiederholen?',
    redoConfirmMessage: 'Dies wird Ihr aktuelles Interview archivieren und eine neue Aufnahmesitzung starten. Ihre vorherige Aufnahme wird aufbewahrt, aber nicht verwendet.',
    redoCancel: 'Aktuelles Behalten',
    redoConfirm: 'Ja, Neu Beginnen',
    redoing: 'Neues Interview wird vorbereitet...',
    redoSuccess: 'Neues Interview erstellt! Weiterleitung...',
    redoError: 'Fehler beim Erstellen des neuen Interviews. Bitte versuchen Sie es erneut.',
  },
};

export function ExpertCompletionScreen({ interview, token, status }: ExpertCompletionScreenProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [transcriptionStatus, setTranscriptionStatus] = useState<string>('pending');
  const [transcript, setTranscript] = useState<any>(null);
  const [showRedoConfirm, setShowRedoConfirm] = useState(false);
  const [isRedoing, setIsRedoing] = useState(false);
  const [redoError, setRedoError] = useState<string | null>(null);

  // Get translation function based on interview language
  const lang = interview.language || 'en';
  const t = (key: string): string => {
    return translations[lang]?.[key] || translations.en[key] || key;
  };

  // Safe values with fallbacks
  const siteName = interview.siteDomain || interview.siteName || t('theSite');
  const expertEmail = interview.expertEmail || t('yourEmail');

  // Poll for transcription status
  useEffect(() => {
    if (status === 'processing') {
      const pollStatus = async () => {
        try {
          const response = await fetch(`/api/expert/interview/transcription?token=${encodeURIComponent(token)}`);
          if (response.ok) {
            const data = await response.json();
            setTranscriptionStatus(data.status || 'pending');
            setTranscript(data);

            // Update progress based on actual status
            // Handle both 'completed' and 'realtime_completed' as completed states
            if (data.status === 'completed' || data.status === 'realtime_completed') {
              setProcessingProgress(100);
            } else if (data.status === 'processing') {
              setProcessingProgress(prev => Math.min(prev + 5, 95));
            } else if (data.status === 'pending') {
              // Slowly increment during pending phase (up to 30%)
              setProcessingProgress(prev => Math.min(prev + 2, 30));
            } else if (data.status === 'failed') {
              setProcessingProgress(100);
            }
          }
        } catch (error) {
          console.error('Error polling transcription status:', error);
        }
      };

      // Initial poll
      pollStatus();

      // Poll every 5 seconds
      const interval = setInterval(pollStatus, 5000);

      return () => clearInterval(interval);
    } else {
      setProcessingProgress(100);
    }
  }, [status, token]);

  const handleSubmitFeedback = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/expert/interview/submit?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: 'Interview completed successfully',
          additionalNotes: ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedoInterview = async () => {
    setIsRedoing(true);
    setRedoError(null);

    try {
      const response = await fetch(`/api/expert/interview/redo?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to redo interview');
      }

      const data = await response.json();

      // Redirect to the new interview
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        // Fallback: construct URL manually
        navigate(`/interview/${data.newInterviewId}?token=${data.newToken}`);
      }
    } catch (error) {
      console.error('Error redoing interview:', error);
      setRedoError(error instanceof Error ? error.message : t('redoError'));
      setShowRedoConfirm(false);
    } finally {
      setIsRedoing(false);
    }
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <CardTitle className="text-2xl font-bold mb-2">
              {t('processingTitle')}
            </CardTitle>
            <p className="text-muted-foreground">
              {t('processingSubtitle')}
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Processing Steps */}
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">{t('recordingUploaded')}</p>
                  <p className="text-sm text-green-700">{t('recordingUploadedDesc')}</p>
                </div>
              </div>

              <div className={`flex items-start gap-3 p-4 rounded-lg ${
                processingProgress > 30
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-muted'
              }`}>
                {processingProgress > 30 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <Loader2 className="w-5 h-5 text-muted-foreground mt-0.5 animate-spin" />
                )}
                <div>
                  <p className={`font-medium ${processingProgress > 30 ? 'text-green-900' : ''}`}>
                    {t('transcribingAudio')}
                  </p>
                  <p className={`text-sm ${processingProgress > 30 ? 'text-green-700' : 'text-muted-foreground'}`}>
                    {t('transcribingAudioDesc')}
                  </p>
                </div>
              </div>

              <div className={`flex items-start gap-3 p-4 rounded-lg ${
                transcriptionStatus === 'completed' || transcriptionStatus === 'realtime_completed' || processingProgress >= 100
                  ? 'bg-green-50 border border-green-200'
                  : transcriptionStatus === 'processing' || processingProgress > 30
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-muted'
              }`}>
                {transcriptionStatus === 'completed' || transcriptionStatus === 'realtime_completed' || processingProgress >= 100 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : transcriptionStatus === 'processing' || processingProgress > 30 ? (
                  <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 animate-spin" />
                ) : (
                  <Loader2 className="w-5 h-5 text-muted-foreground mt-0.5 animate-spin" />
                )}
                <div>
                  <p className={`font-medium ${
                    transcriptionStatus === 'completed' || transcriptionStatus === 'realtime_completed' || processingProgress >= 100
                      ? 'text-green-900'
                      : transcriptionStatus === 'processing' || processingProgress > 30
                      ? 'text-blue-900'
                      : ''
                  }`}>
                    {t('processingComplete')}
                  </p>
                  <p className={`text-sm ${
                    transcriptionStatus === 'completed' || transcriptionStatus === 'realtime_completed' || processingProgress >= 100
                      ? 'text-green-700'
                      : transcriptionStatus === 'processing' || processingProgress > 30
                      ? 'text-blue-700'
                      : 'text-muted-foreground'
                  }`}>
                    {transcriptionStatus === 'completed' || transcriptionStatus === 'realtime_completed' ?
                      t('transcriptionComplete') :
                      transcriptionStatus === 'processing' ?
                      t('processingInterview') :
                      t('preparingProcess')}
                  </p>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('overallProgress')}</span>
                <span>{Math.round(processingProgress)}%</span>
              </div>
              <Progress value={processingProgress} className="h-2" />
            </div>

            {/* Info */}
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                {t('processingTime')}
              </AlertDescription>
            </Alert>

            {/* Redo Option - Show when processing is complete but status hasn't changed */}
            {(transcriptionStatus === 'completed' || transcriptionStatus === 'realtime_completed' || processingProgress >= 100) && (
              <>
                {/* Redo Confirmation Dialog */}
                {showRedoConfirm && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-md">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-orange-500" />
                          {t('redoConfirmTitle')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-muted-foreground">
                          {t('redoConfirmMessage')}
                        </p>
                        <Alert className="border-orange-200 bg-orange-50">
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                          <AlertDescription className="text-orange-900">
                            {t('redoWarning')}
                          </AlertDescription>
                        </Alert>
                        <div className="flex gap-3 justify-end pt-2">
                          <Button
                            variant="outline"
                            onClick={() => setShowRedoConfirm(false)}
                            disabled={isRedoing}
                          >
                            {t('redoCancel')}
                          </Button>
                          <Button
                            onClick={handleRedoInterview}
                            disabled={isRedoing}
                            className="bg-orange-600 hover:bg-orange-700"
                          >
                            {isRedoing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {t('redoing')}
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                {t('redoConfirm')}
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {redoError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{redoError}</AlertDescription>
                  </Alert>
                )}

                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-orange-900 mb-1">{t('redoInterview')}</p>
                      <p className="text-sm text-orange-700 mb-3">
                        {t('redoDescription')}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowRedoConfirm(true)}
                        className="border-orange-300 text-orange-700 hover:bg-orange-100"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('redoInterview')}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completed status
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-3xl font-bold mb-2">
            {t('interviewComplete')}
          </CardTitle>
          <p className="text-lg text-muted-foreground">
            {t('thankYou')}, {interview.expertName}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Redo Confirmation Dialog */}
          {showRedoConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-orange-500" />
                    {t('redoConfirmTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    {t('redoConfirmMessage')}
                  </p>
                  <Alert className="border-orange-200 bg-orange-50">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-900">
                      {t('redoWarning')}
                    </AlertDescription>
                  </Alert>
                  <div className="flex gap-3 justify-end pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowRedoConfirm(false)}
                      disabled={isRedoing}
                    >
                      {t('redoCancel')}
                    </Button>
                    <Button
                      onClick={handleRedoInterview}
                      disabled={isRedoing}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {isRedoing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('redoing')}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {t('redoConfirm')}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!submitted ? (
            <>
              {/* Redo Error Alert */}
              {redoError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{redoError}</AlertDescription>
                </Alert>
              )}

              {/* Success Message */}
              <Alert className="border-green-200 bg-green-50">
                <Sparkles className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  {t('successMessage')}
                </AlertDescription>
              </Alert>

              {/* Redo Option */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-orange-900 mb-1">{t('redoInterview')}</p>
                    <p className="text-sm text-orange-700 mb-3">
                      {t('redoDescription')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRedoConfirm(true)}
                      className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('redoInterview')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Transcript and Article Preview */}
              {transcript && (transcript.transcriptText || transcript.articleContent) && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">{t('interviewContent')}</h3>

                  {transcript.transcriptText && (
                    <div className="bg-muted p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-5 h-5 text-primary" />
                        <h4 className="font-medium">{t('transcript')}</h4>
                      </div>
                      <div className="text-sm text-muted-foreground max-h-96 overflow-y-auto whitespace-pre-wrap">
                        {transcript.transcriptText}
                      </div>
                    </div>
                  )}

                  {transcript.articleContent && (
                    <div className="bg-muted p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-5 h-5 text-primary" />
                        <h4 className="font-medium">{t('generatedArticle')}</h4>
                      </div>
                      <div className="text-sm text-muted-foreground max-h-96 overflow-y-auto whitespace-pre-wrap">
                        {transcript.articleContent}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* What's Next */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{t('whatHappensNext')}</h3>

                <div className="space-y-3">
                  <div className="flex gap-3 p-4 bg-muted rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="font-medium">{t('articleReview')}</p>
                      <p className="text-sm text-muted-foreground">
                        {interview.journalist.name} {t('articleReviewDesc')}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 p-4 bg-muted rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="font-medium">{t('expertApproval')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('expertApprovalDesc')}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 p-4 bg-muted rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="font-medium">{t('publication')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('publicationDesc')} <strong>{siteName}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900 mb-1">{t('checkEmail')}</p>
                    <p className="text-sm text-blue-700">
                      {t('emailUpdates')} <strong>{expertEmail}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Final Action */}
              <div className="text-center pt-4">
                <Button
                  onClick={handleSubmitFeedback}
                  disabled={isSubmitting}
                  size="lg"
                  className="px-8"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('submitting')}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('confirmCompletion')}
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* After Submission */}
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-900 font-medium">
                  {t('thankYouSubmitted')}
                </AlertDescription>
              </Alert>

              <div className="text-center py-8">
                <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">{t('allDone')}</h3>
                <p className="text-muted-foreground mb-6">
                  {t('closeWindow')}
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
